<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import BarChart from 'shared/components/charts/BarChart.vue';
import Spinner from 'shared/components/Spinner.vue';
import { downloadCsvFile, generateFileName } from 'dashboard/helper/downloadHelper';
import MetaReportsAPI from 'dashboard/api/metaReports';
import {
  fmt,
  fmtPct,
  rateColorClass,
  DELIVERY_THRESHOLD,
} from '../helper/metaFormatters';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  since: { type: Number, required: true },
  until: { type: Number, required: true },
});

const { t } = useI18n();

const isLoading = ref(false);
const messages = ref({ data_points: [], totals: { sent: 0, delivered: 0 } });
// Totales del período anterior (misma longitud) para el delta.
const previous = ref({ sent: 0, delivered: 0, hasData: false });

const dailyRows = computed(() =>
  (messages.value.data_points || []).map(dp => {
    const sent = dp.sent || 0;
    const delivered = dp.delivered || 0;
    const undelivered = sent - delivered;
    const rate = sent > 0 ? (delivered / sent) * 100 : 0;
    return {
      start: dp.start,
      day: format(fromUnixTime(dp.start), 'dd/MM/yyyy'),
      sent,
      delivered,
      undelivered,
      rate,
      // Artefacto de webhooks cruzando la medianoche: delivered > sent.
      negativeGap: delivered > sent,
      belowThreshold: sent > 0 && rate < DELIVERY_THRESHOLD,
    };
  })
);

const totals = computed(() => {
  const sent = messages.value.totals?.sent || 0;
  const delivered = messages.value.totals?.delivered || 0;
  const undelivered = sent - delivered;
  const rate = sent > 0 ? (delivered / sent) * 100 : 0;
  const days = dailyRows.value.length || 0;
  const peak = dailyRows.value.reduce(
    (max, r) => (r.sent > max.sent ? r : max),
    { sent: 0, day: '—' }
  );
  return {
    sent,
    delivered,
    undelivered,
    rate,
    undeliveredPct: sent > 0 ? (undelivered / sent) * 100 : 0,
    avgDaily: days > 0 ? sent / days : 0,
    peakDay: peak.day,
    peakValue: peak.sent,
  };
});

// mode: 'pct' variación porcentual, 'pp' puntos porcentuales. upIsGood: subir = verde.
const buildDelta = (current, prev, mode, upIsGood) => {
  if (!previous.value.hasData) return null;
  if (mode === 'pct' && !prev) return null; // sin base de comparación

  const diff = current - prev;
  const value = mode === 'pp' ? diff : (diff / prev) * 100;
  const up = diff > 0;
  const flat = Math.abs(diff) < 0.05;
  const good = upIsGood ? diff >= 0 : diff <= 0;
  const sign = up ? '+' : diff < 0 ? '−' : '';
  const magnitude = new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 1,
  }).format(Math.abs(value));
  return {
    label: `${sign}${magnitude}${mode === 'pp' ? ' pp' : '%'}`,
    arrow: flat ? '→' : up ? '▲' : '▼',
    flat,
    good,
  };
};

const prevRate = computed(() =>
  previous.value.sent > 0
    ? (previous.value.delivered / previous.value.sent) * 100
    : 0
);
const prevUndelivered = computed(
  () => previous.value.sent - previous.value.delivered
);

const deltas = computed(() => ({
  sent: buildDelta(totals.value.sent, previous.value.sent, 'pct', true),
  delivered: buildDelta(
    totals.value.delivered,
    previous.value.delivered,
    'pct',
    true
  ),
  rate: buildDelta(totals.value.rate, prevRate.value, 'pp', true),
  undelivered: buildDelta(
    totals.value.undelivered,
    prevUndelivered.value,
    'pct',
    false
  ),
}));

const deltaClass = delta => {
  if (!delta || delta.flat) return 'text-n-slate-11';
  return delta.good
    ? 'text-green-600 dark:text-green-500'
    : 'text-red-600 dark:text-red-500';
};

// No entregados (barras) + Tasa % (línea); Enviados va en el tooltip por escala.
const trendData = computed(() => ({
  labels: dailyRows.value.map(row => row.day),
  datasets: [
    {
      type: 'bar',
      label: t('META_REPORTS.MESSAGES.UNDELIVERED'),
      data: dailyRows.value.map(row => row.undelivered),
      backgroundColor: '#ef4444',
      yAxisID: 'y',
      order: 2,
    },
    {
      type: 'line',
      label: t('META_REPORTS.KPI.DELIVERY_RATE'),
      // Sin envíos → null (hueco) en vez de 0% para no falsear la tasa.
      data: dailyRows.value.map(row =>
        row.sent > 0 ? Number(row.rate.toFixed(2)) : null
      ),
      spanGaps: false,
      borderColor: '#10b981',
      backgroundColor: '#10b981',
      yAxisID: 'y1',
      fill: false,
      pointRadius: 0,
      tension: 0.3,
      borderWidth: 2,
      order: 1,
    },
  ],
}));

const trendOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: true, labels: { color: '#8b95a5' } },
    tooltip: {
      callbacks: {
        label: ctx =>
          ctx.dataset.yAxisID === 'y1'
            ? `${ctx.dataset.label}: ${fmtPct(ctx.parsed.y)}`
            : `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        afterBody: items => {
          const idx = items[0]?.dataIndex;
          if (idx == null) return '';
          const row = dailyRows.value[idx];
          return `${t('META_REPORTS.MESSAGES.SENT')}: ${fmt(row.sent)}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        color: '#8b95a5',
        autoSkip: true,
        maxTicksLimit: 16,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      position: 'left',
      title: {
        display: true,
        text: t('META_REPORTS.MESSAGES.UNDELIVERED'),
        color: '#8b95a5',
      },
      ticks: { color: '#8b95a5', precision: 0, callback: value => fmt(value) },
    },
    y1: {
      position: 'right',
      grid: { drawOnChartArea: false },
      title: {
        display: true,
        text: t('META_REPORTS.KPI.DELIVERY_RATE'),
        color: '#8b95a5',
      },
      ticks: { color: '#8b95a5', callback: value => `${value}%` },
    },
  },
}));

const fetchMessages = async () => {
  if (!props.inboxId) return;
  const params = { inboxId: props.inboxId, since: props.since, until: props.until };
  // Período anterior: misma longitud, terminando justo antes del inicio.
  const span = props.until - props.since;
  const prevParams = {
    inboxId: props.inboxId,
    since: props.since - span - 1,
    until: props.since - 1,
  };

  isLoading.value = true;
  const [msgRes, prevRes] = await Promise.allSettled([
    MetaReportsAPI.getMessages(params),
    MetaReportsAPI.getMessages(prevParams),
  ]);

  if (msgRes.status === 'fulfilled') {
    messages.value = msgRes.value.data;
  } else {
    messages.value = { data_points: [], totals: { sent: 0, delivered: 0 } };
  }

  if (prevRes.status === 'fulfilled' && !prevRes.value.data.error) {
    const prevTotals = prevRes.value.data.totals || { sent: 0, delivered: 0 };
    previous.value = {
      sent: prevTotals.sent || 0,
      delivered: prevTotals.delivered || 0,
      hasData: true,
    };
  } else {
    previous.value = { sent: 0, delivered: 0, hasData: false };
  }

  isLoading.value = false;
};

const exportCsv = () => {
  const header = [
    t('META_REPORTS.MESSAGES.DAY'),
    t('META_REPORTS.MESSAGES.SENT'),
    t('META_REPORTS.MESSAGES.DELIVERED'),
    t('META_REPORTS.MESSAGES.UNDELIVERED'),
    t('META_REPORTS.MESSAGES.RATE'),
  ];
  const lines = dailyRows.value.map(r =>
    [r.day, r.sent, r.delivered, r.undelivered, `${r.rate.toFixed(1)}%`].join(',')
  );
  const totalLine = [
    t('META_REPORTS.MESSAGES.TOTAL'),
    totals.value.sent,
    totals.value.delivered,
    totals.value.undelivered,
    `${totals.value.rate.toFixed(1)}%`,
  ].join(',');
  const content = [header.join(','), ...lines, totalLine].join('\n');
  downloadCsvFile(
    generateFileName({ type: 'meta-messages', to: props.until }),
    content
  );
};

watch(
  () => [props.inboxId, props.since, props.until],
  fetchMessages,
  { immediate: true }
);
</script>

<template>
  <div v-if="isLoading" class="flex justify-center py-10">
    <Spinner size="" />
  </div>

  <div v-else class="flex flex-col gap-8">
    <!-- 1. Fila de KPIs -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.MESSAGES.SENT') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmt(totals.sent) }}
        </p>
        <p
          v-if="deltas.sent"
          v-tooltip="$t('META_REPORTS.KPI.DELTA_TOOLTIP')"
          class="text-xs mt-1"
          :class="deltaClass(deltas.sent)"
        >
          {{ deltas.sent.arrow }} {{ deltas.sent.label }}
        </p>
      </div>

      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.MESSAGES.DELIVERED') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmt(totals.delivered) }}
        </p>
        <p
          v-if="deltas.delivered"
          v-tooltip="$t('META_REPORTS.KPI.DELTA_TOOLTIP')"
          class="text-xs mt-1"
          :class="deltaClass(deltas.delivered)"
        >
          {{ deltas.delivered.arrow }} {{ deltas.delivered.label }}
        </p>
      </div>

      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.KPI.DELIVERY_RATE') }}
        </span>
        <p class="text-2xl font-semibold mt-1" :class="rateColorClass(totals.rate)">
          {{ fmtPct(totals.rate) }}
        </p>
        <p
          v-if="deltas.rate"
          v-tooltip="$t('META_REPORTS.KPI.DELTA_TOOLTIP')"
          class="text-xs mt-1"
          :class="deltaClass(deltas.rate)"
        >
          {{ deltas.rate.arrow }} {{ deltas.rate.label }}
        </p>
      </div>

      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.MESSAGES.UNDELIVERED') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmt(totals.undelivered) }}
          <span class="text-sm font-normal text-n-slate-11">
            ({{ fmtPct(totals.undeliveredPct) }})
          </span>
        </p>
        <p
          v-if="deltas.undelivered"
          v-tooltip="$t('META_REPORTS.KPI.DELTA_TOOLTIP')"
          class="text-xs mt-1"
          :class="deltaClass(deltas.undelivered)"
        >
          {{ deltas.undelivered.arrow }} {{ deltas.undelivered.label }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap gap-x-8 gap-y-2 text-sm -mt-4">
      <div>
        <span class="text-n-slate-11">{{ $t('META_REPORTS.KPI.AVG_DAILY') }}:</span>
        <span class="font-medium text-n-slate-12">{{ fmt(totals.avgDaily) }}</span>
      </div>
      <div>
        <span class="text-n-slate-11">{{ $t('META_REPORTS.KPI.PEAK_DAY') }}:</span>
        <span class="font-medium text-n-slate-12">
          {{ totals.peakDay }} ({{ fmt(totals.peakValue) }})
        </span>
      </div>
    </div>

    <!-- 2. Gráfico de tendencia diaria -->
    <div
      class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
    >
      <h3 class="text-lg font-medium text-n-slate-12 mb-4">
        {{ $t('META_REPORTS.CHART.TITLE') }}
      </h3>
      <div class="h-72">
        <div
          v-if="!dailyRows.length"
          class="flex items-center justify-center h-full text-sm text-n-slate-11"
        >
          {{ $t('META_REPORTS.NO_DATA') }}
        </div>
        <BarChart v-else :collection="trendData" :chart-options="trendOptions" />
      </div>
    </div>

    <!-- 3. Tabla diaria -->
    <div>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ $t('META_REPORTS.MESSAGES.TITLE') }}
        </h3>
        <button
          v-if="dailyRows.length"
          class="text-sm text-n-blue-text hover:underline"
          @click="exportCsv"
        >
          {{ $t('META_REPORTS.EXPORT_CSV') }}
        </button>
      </div>

      <div
        v-if="dailyRows.length"
        class="max-h-96 overflow-auto border border-n-weak rounded-lg"
      >
        <table class="w-full text-sm border-collapse">
          <thead
            class="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-n-weak"
          >
            <tr class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
              <th class="text-left py-2.5 px-4">
                {{ $t('META_REPORTS.MESSAGES.DAY') }}
              </th>
              <th class="text-right py-2.5 px-3">
                {{ $t('META_REPORTS.MESSAGES.SENT') }}
              </th>
              <th class="text-right py-2.5 px-3">
                {{ $t('META_REPORTS.MESSAGES.DELIVERED') }}
              </th>
              <th class="text-right py-2.5 px-3">
                {{ $t('META_REPORTS.MESSAGES.UNDELIVERED') }}
              </th>
              <th class="text-right py-2.5 px-4">
                {{ $t('META_REPORTS.MESSAGES.RATE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in dailyRows"
              :key="row.start"
              class="border-b border-n-weak last:border-0 text-n-slate-12"
              :class="{
                'bg-amber-50 dark:bg-amber-950/30':
                  row.belowThreshold || row.negativeGap,
              }"
            >
              <td class="py-2.5 px-4 whitespace-nowrap">
                {{ row.day }}
                <fluent-icon
                  v-if="row.negativeGap"
                  v-tooltip="$t('META_REPORTS.MESSAGES.NEGATIVE_GAP_INFO')"
                  size="14"
                  icon="info"
                  class="text-amber-600 inline-block ml-1 align-middle"
                />
              </td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(row.sent) }}
              </td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(row.delivered) }}
              </td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(row.undelivered) }}
              </td>
              <td
                class="py-2.5 px-4 text-right tabular-nums"
                :class="rateColorClass(row.rate)"
              >
                {{ fmtPct(row.rate) }}
              </td>
            </tr>
          </tbody>
          <tfoot
            class="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-n-weak font-medium text-n-slate-12"
          >
            <tr>
              <td class="py-2.5 px-4">{{ $t('META_REPORTS.MESSAGES.TOTAL') }}</td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(totals.sent) }}
              </td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(totals.delivered) }}
              </td>
              <td class="py-2.5 px-3 text-right tabular-nums">
                {{ fmt(totals.undelivered) }}
              </td>
              <td
                class="py-2.5 px-4 text-right tabular-nums"
                :class="rateColorClass(totals.rate)"
              >
                {{ fmtPct(totals.rate) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p v-else class="text-n-slate-11 py-4">{{ $t('META_REPORTS.NO_DATA') }}</p>
    </div>
  </div>
</template>
